import React, { useState } from "react";
import { isAfter, isSameDay, format } from "date-fns";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { updateDateFilter } from "../../slices/dashboardSlice";
import {
  getPresetDateRange,
  computePriorDateRange,
  PRESET_LABELS,
  ALL_PRESETS,
  type DatePreset,
} from "../../utils/dateFilterUtils";
import Calendar from "./Calendar";

type CustomMode = "single" | "range";

function pillCls(active: boolean, withIcon = false) {
  return [
    withIcon ? "inline-flex items-center gap-1" : "",
    "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
    active
      ? "bg-primary text-white border-primary"
      : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary",
  ]
    .filter(Boolean)
    .join(" ");
}

const DashboardDateFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);

  const [showCustom, setShowCustom] = useState(false);
  const [customMode, setCustomMode] = useState<CustomMode>("range");

  // Single-date selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Range selection
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);

  const today = new Date();

  // ── Handlers ────────────────────────────────────────────────

  const handlePreset = (preset: DatePreset) => {
    const { startDate, endDate } = getPresetDateRange(preset);
    const { priorStartDate, priorEndDate } = computePriorDateRange(
      preset,
      new Date(startDate),
      new Date(endDate)
    );
    dispatch(updateDateFilter({ startDate, endDate, priorStartDate, priorEndDate, preset }));
    setShowCustom(false);
  };

  const handleAllTime = () => {
    dispatch(updateDateFilter({ startDate: null, endDate: null, priorStartDate: null, priorEndDate: null, preset: null }));
    setShowCustom(false);
  };

  const handleModeChange = (mode: CustomMode) => {
    setCustomMode(mode);
    setSelectedDate(null);
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleDayClick = (day: Date) => {
    if (customMode === "single") {
      setSelectedDate(day);
    } else {
      // Range: 1st click = start, 2nd click (after start) = end; otherwise restart
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(day);
        setRangeEnd(null);
      } else if (isAfter(day, rangeStart)) {
        setRangeEnd(day);
      } else {
        // Clicked before or on start — restart selection
        setRangeStart(day);
        setRangeEnd(null);
      }
    }
  };

  const handleApply = () => {
    if (customMode === "single" && selectedDate) {
      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(23, 59, 59, 999);
      const { priorStartDate, priorEndDate } = computePriorDateRange("custom", start, end);
      dispatch(
        updateDateFilter({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          priorStartDate,
          priorEndDate,
          preset: "custom",
        })
      );
    } else if (customMode === "range" && rangeStart && rangeEnd) {
      const start = new Date(rangeStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(rangeEnd);
      end.setHours(23, 59, 59, 999);
      const { priorStartDate, priorEndDate } = computePriorDateRange("custom", start, end);
      dispatch(
        updateDateFilter({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          priorStartDate,
          priorEndDate,
          preset: "custom",
        })
      );
    }
    setShowCustom(false);
  };

  const canApply =
    customMode === "single" ? !!selectedDate : !!(rangeStart && rangeEnd);

  // Label shown on the active-filter clear badge
  const activeLabel =
    dateFilter.preset === null
      ? null
      : dateFilter.preset === "custom"
      ? dateFilter.startDate
        ? (() => {
            const s = new Date(dateFilter.startDate);
            const e = dateFilter.endDate ? new Date(dateFilter.endDate) : s;
            return isSameDay(s, e)
              ? format(s, "MMM d, yyyy")
              : `${format(s, "MMM d")} – ${format(e, "MMM d, yyyy")}`;
          })()
        : "Custom"
      : PRESET_LABELS[dateFilter.preset as DatePreset];

  const rangeHint =
    customMode === "range"
      ? !rangeStart
        ? "Click a start date"
        : !rangeEnd
        ? "Now click an end date"
        : `${format(rangeStart, "MMM d")} – ${format(rangeEnd, "MMM d, yyyy")}`
      : selectedDate
      ? format(selectedDate, "MMMM d, yyyy")
      : "Click a date";

  return (
    <div className="space-y-3">
      {/* ── Preset pill row ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" onClick={handleAllTime} className={pillCls(!dateFilter.preset)}>
          All Time
        </button>

        {ALL_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => handlePreset(preset)}
            className={pillCls(dateFilter.preset === preset)}
          >
            {PRESET_LABELS[preset]}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setShowCustom((v) => !v)}
          className={pillCls(dateFilter.preset === "custom", true)}
        >
          Custom
          {showCustom ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Active filter badge */}
        {dateFilter.preset !== null && (
          <button
            type="button"
            onClick={handleAllTime}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-gray-500 border border-gray-200 hover:border-red-300 hover:text-red-500 transition-colors"
            title="Clear date filter"
          >
            <X size={12} />
            {activeLabel}
          </button>
        )}
      </div>

      {/* ── Custom panel ── */}
      {showCustom && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-md p-4 space-y-4 inline-block">
          {/* Mode radio */}
          <div className="flex gap-4">
            {(["single", "range"] as CustomMode[]).map((m) => (
              <label
                key={m}
                className="flex items-center gap-1.5 text-sm cursor-pointer"
              >
                <input
                  type="radio"
                  name="customMode"
                  value={m}
                  checked={customMode === m}
                  onChange={() => handleModeChange(m)}
                  className="accent-primary"
                />
                {m === "single" ? "Specific Date" : "Date Range"}
              </label>
            ))}
          </div>

          {/* Calendar */}
          <Calendar
            mode={customMode}
            selectedDate={selectedDate}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onDayClick={handleDayClick}
            maxDate={today}
          />

          {/* Selection hint */}
          <p className="text-xs text-gray-400">{rangeHint}</p>

          {/* Apply */}
          <button
            type="button"
            onClick={handleApply}
            disabled={!canApply}
            className="w-full py-2 bg-primary text-white text-sm rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardDateFilter;
